import { useCallback, useRef, useState, useEffect } from 'react'
import { type EagerResponse, useApolloRequestWrapper } from '../../../api/useApolloRequestWrapper'
import {
  type LazyResponse,
  useApolloRequestLazyWrapper,
} from '../../../api/useApolloRequestLazyWrapper'
import { type PaginatedLazyResponse } from '../../../api'
import {
  type GetDashboardOrderSearchResultListQuery,
  type GetDashboardOrderSearchResultListQueryVariables,
  useGetDashboardOrderSearchResultListLazyQuery,
  useGetDashboardOrderSearchResultListQuery,
} from '../../../../api'
import { type TrackingEventMetadata } from '../../../types'
import { type DashboardOrder, type PageInfo, type DashboardOrderSearchResultList } from './types'
import { useLogRequest } from './logging/hooks'

function mapResponse(data: GetDashboardOrderSearchResultListQuery): DashboardOrderSearchResultList {
  // TODO: do actual validation once zod/other validation library is available
  const results =
    data
      ?.instacart_enterprise_frigate_fulfillment_v1_DashboardOrderService_GetDashboardOrderSearchResultList
      ?.results ?? []
  const pageInfo =
    data
      ?.instacart_enterprise_frigate_fulfillment_v1_DashboardOrderService_GetDashboardOrderSearchResultList
      ?.pageInfo

  return {
    results: results as DashboardOrder[],
    pageInfo: pageInfo as PageInfo,
  }
}

export const useGetDashboardOrderSearchResultListEagerRequest = ({
  queryParams,
}: {
  queryParams: GetDashboardOrderSearchResultListQueryVariables
}): EagerResponse<DashboardOrderSearchResultList> => {
  const query = useGetDashboardOrderSearchResultListQuery({
    variables: queryParams,
    refetchWritePolicy: 'overwrite',
  })
  return useApolloRequestWrapper({ query, mapResponse })
}

export const useGetDashboardOrderSearchResultListRequest = ({
  queryParams,
  retailerId,
  selectedStoreConfigurationId,
  trackEvent,
}: {
  queryParams: GetDashboardOrderSearchResultListQueryVariables
  retailerId: string
  selectedStoreConfigurationId?: string
  trackEvent: (event: string, metadata: TrackingEventMetadata) => void
}): LazyResponse<
  DashboardOrderSearchResultList,
  GetDashboardOrderSearchResultListQueryVariables
> => {
  const resultTuple = useGetDashboardOrderSearchResultListLazyQuery({
    fetchPolicy: 'network-only',
    refetchWritePolicy: 'overwrite',
  })
  const requestData = useApolloRequestLazyWrapper({ resultTuple, mapResponse })

  useLogRequest({ requestData, queryParams, retailerId, selectedStoreConfigurationId, trackEvent })

  return requestData
}

export const usePaginatedGetDashboardOrderSearchResultListRequest = ({
  queryParams,
  retailerId,
  selectedStoreConfigurationId,
  trackEvent,
}: {
  queryParams: GetDashboardOrderSearchResultListQueryVariables
  retailerId: string
  selectedStoreConfigurationId?: string
  trackEvent: (event: string, metadata: TrackingEventMetadata) => void
}): PaginatedLazyResponse<
  DashboardOrderSearchResultList,
  GetDashboardOrderSearchResultListQueryVariables
> => {
  const [cursorHistory, setCursorHistory] = useState<string[]>([])
  const [pageIndex, setPageIndex] = useState<number>(0)
  const paramsRef = useRef<string>('')

  const requestData = useGetDashboardOrderSearchResultListRequest({
    queryParams,
    retailerId,
    selectedStoreConfigurationId,
    trackEvent,
  })

  const executeQuery = useCallback(
    (options?: { variables?: GetDashboardOrderSearchResultListQueryVariables }) => {
      const stringifiedParams = JSON.stringify(options?.variables)

      if (
        stringifiedParams !== paramsRef.current &&
        options?.variables?.paginationInfo?.after === undefined
      ) {
        setCursorHistory([])
        setPageIndex(0)
        paramsRef.current = stringifiedParams
      }

      requestData.execute(options)
    },
    [requestData]
  )

  useEffect(() => {
    if (requestData.type === 'success' && requestData.data.pageInfo.endCursor) {
      const currentCursor = requestData.data.pageInfo.endCursor

      if (cursorHistory[pageIndex] !== currentCursor) {
        setCursorHistory(prev => [...prev.slice(0, pageIndex), currentCursor])
      }
    }
  }, [requestData, pageIndex, cursorHistory])

  const fetchNextPage = useCallback(() => {
    if (requestData.type !== 'success' || !requestData.data.pageInfo.hasNextPage) {
      return
    }

    const nextPageIndex = pageIndex + 1
    setPageIndex(nextPageIndex)

    executeQuery({
      variables: {
        ...queryParams,
        paginationInfo: {
          ...queryParams.paginationInfo,
          after: requestData.data.pageInfo.endCursor,
        },
      },
    })
  }, [requestData, queryParams, pageIndex, executeQuery])

  const fetchPreviousPage = useCallback(() => {
    if (pageIndex <= 0) {
      return
    }

    const prevPageIndex = pageIndex - 1
    setPageIndex(prevPageIndex)

    const prevCursor = prevPageIndex > 0 ? cursorHistory[prevPageIndex - 1] : undefined

    executeQuery({
      variables: {
        ...queryParams,
        paginationInfo: {
          ...queryParams.paginationInfo,
          after: prevCursor,
        },
      },
    })
  }, [pageIndex, cursorHistory, queryParams, executeQuery])

  return {
    ...requestData,
    execute: executeQuery,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage: pageIndex > 0,
    hasNextPage: requestData.type === 'success' ? requestData.data.pageInfo.hasNextPage : false,
    currentPage: pageIndex + 1,
  }
}
